import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Treemap,
} from 'recharts';
import { motion } from 'framer-motion';
import { Monitor, Car, Layers, List, LayoutGrid } from 'lucide-react';
import { PlatformIcon, normalizePlatform, normalizePlatformLabel } from '@/components/PlatformIcon';
import { ChartCard } from './ChartCard';
import { fetchBreakdownData } from '../api';
import type { DashboardFilters } from '../DashboardPage';

const PLATFORM_BRAND_COLORS: Record<string, string> = {
  meta: '#0082FB',
  linkedin: '#0A66C2',
  tiktok: '#010101',
  reddit: '#FF4500',
  snapchat: '#FFFC00',
  google: '#4285F4',
  'google ads': '#4285F4',
  microsoft: '#00A4EF',
  other: '#94a3b8',
};

function getPlatformColor(platform: string): string {
  return PLATFORM_BRAND_COLORS[normalizePlatform(platform)] || '#94a3b8';
}

type FormatView = 'list' | 'treemap';

interface BreakdownTabProps {
  filters: DashboardFilters;
}

export function BreakdownTab({ filters }: BreakdownTabProps) {
  const [formatView, setFormatView] = useState<FormatView>('list');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'breakdown', filters],
    queryFn: () =>
      fetchBreakdownData({
        campaign_status: filters.campaignStatus,
        market: filters.market || undefined,
      }),
    staleTime: 2 * 60_000,
  });

  const platformData = data?.platform_breakdown ?? [];
  const platformFormatData = useMemo(
    () => data?.platform_format_breakdown ?? [],
    [data?.platform_format_breakdown],
  );
  const nameplateData = data?.nameplate_breakdown ?? [];

  const platformTotal = platformData.reduce((sum, d) => sum + d.count, 0);
  const nameplateMax = nameplateData.length > 0 ? nameplateData[0].count : 1;

  const treemapData = useMemo(
    () => ({
      name: 'root',
      children: platformFormatData.map((p) => ({
        name: p.platform,
        children: p.formats.map((f) => ({
          name: f.name,
          size: f.count,
          platform: p.platform,
          color: getPlatformColor(p.platform),
        })),
      })),
    }),
    [platformFormatData],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex gap-5 p-6 overflow-hidden" style={{ height: '100%' }}>
      {/* Left column — Platform donut + Nameplate stacked 50/50 */}
      <div className="flex-[2] flex flex-col gap-5 min-h-0">
        {/* Platform donut */}
        <div style={{ height: '50%' }}>
          <ChartCard
            icon={Monitor}
            title="By Platform"
            subtitle="Campaign distribution by platform"
            className="h-full"
            footer={
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                {platformData.map((d) => {
                  const pct =
                    platformTotal > 0 ? ((d.count / platformTotal) * 100).toFixed(1) : '0';
                  return (
                    <div
                      key={d.name}
                      className="flex items-center gap-1.5 text-[11px] text-slate-600"
                    >
                      <PlatformIcon platform={d.name} className="w-3 h-3" showLabel={false} />
                      <span className="font-medium">{normalizePlatformLabel(d.name)}</span>
                      <span className="text-slate-400">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            }
          >
            {platformData.length > 0 ? (
              <ResponsiveContainer key={`donut-${filters.animationKey}`} width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={platformData as unknown as Record<string, unknown>[]}
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    paddingAngle={1.5}
                    dataKey="count"
                    nameKey="name"
                    strokeWidth={0}
                    animationBegin={0}
                    animationDuration={800}
                  >
                    {platformData.map((d) => (
                      <Cell
                        key={d.name}
                        fill={getPlatformColor(d.name)}
                        className="transition-opacity duration-200 hover:opacity-80 cursor-pointer"
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const entry = payload[0].payload as { name: string; count: number };
                      const pct =
                        platformTotal > 0
                          ? ((entry.count / platformTotal) * 100).toFixed(1)
                          : '0.0';
                      return (
                        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600 min-w-[160px]">
                          <div className="flex items-center gap-2 font-semibold mb-2">
                            <PlatformIcon
                              platform={entry.name}
                              className="w-3.5 h-3.5"
                              showLabel={false}
                            />
                            <span>{normalizePlatformLabel(entry.name)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Campaigns:</span>
                            <span className="font-mono font-medium">
                              {entry.count.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Share:</span>
                            <span className="font-mono font-medium">{pct}%</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No platform data
              </div>
            )}
          </ChartCard>
        </div>

        {/* Nameplate list */}
        <div style={{ height: 'calc(50% - 20px)' }}>
          <ChartCard
            icon={Car}
            title="By Nameplate"
            subtitle="Top vehicle models"
            className="h-full"
          >
            <div
              key={`np-${filters.animationKey}`}
              className="h-full overflow-y-auto hover-scroll pr-3"
            >
              {nameplateData.map((item, i) => (
                <motion.div
                  key={item.name}
                  className="flex items-center gap-3 py-1.5"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.03, ease: [0.4, 0, 0.2, 1] }}
                >
                  <span className="text-[10px] font-medium text-slate-400 w-5 text-right">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-medium text-slate-700 truncate">
                        {item.name || '—'}
                      </span>
                      <span className="text-xs font-semibold text-slate-600 ml-2 flex-shrink-0">
                        {item.count}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex justify-end">
                      <motion.div
                        className="h-full rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(item.count / nameplateMax) * 100}%` }}
                        transition={{
                          duration: 0.5,
                          delay: i * 0.03 + 0.2,
                          ease: [0.4, 0, 0.2, 1],
                        }}
                        style={{ backgroundColor: '#93c5fd' }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Right — Format breakdown */}
      <div className="flex-[3]">
        <ChartCard
          icon={Layers}
          title="By Format"
          subtitle="Format breakdown within each platform"
          controls={
            <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5">
              {[
                { id: 'list' as const, label: 'List', icon: List },
                { id: 'treemap' as const, label: 'Treemap', icon: LayoutGrid },
              ].map((view) => (
                <button
                  key={view.id}
                  onClick={() => setFormatView(view.id)}
                  className={`h-7 px-3 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                    formatView === view.id
                      ? 'bg-[#0014cc] text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <view.icon className="w-3.5 h-3.5" />
                  {view.label}
                </button>
              ))}
            </div>
          }
          footer={
            formatView === 'treemap' ? (
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                {platformFormatData.map((p) => (
                  <div
                    key={p.platform}
                    className="flex items-center gap-1.5 text-[11px] text-slate-600"
                  >
                    <PlatformIcon platform={p.platform} className="w-3 h-3" showLabel={false} />
                    <span>{p.platform}</span>
                  </div>
                ))}
              </div>
            ) : undefined
          }
        >
          {formatView === 'list' ? (
            <div
              key={`fmt-${filters.animationKey}`}
              className="h-full overflow-y-auto hover-scroll pr-2 flex flex-col gap-1"
            >
              {platformFormatData.map((p, pIdx) => {
                const color = getPlatformColor(p.platform);
                return (
                  <motion.div
                    key={p.platform}
                    className="mb-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: pIdx * 0.08, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <div className="flex items-center gap-2.5 px-3 py-2 mb-0.5">
                      <div
                        className="w-1 h-6 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <div className="w-6 h-6 bg-slate-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <PlatformIcon
                          platform={p.platform}
                          className="w-3.5 h-3.5"
                          showLabel={false}
                        />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{p.platform}</span>
                      <span className="text-xs text-slate-400 ml-auto tabular-nums">
                        {p.total.toLocaleString()} total
                      </span>
                    </div>

                    {p.formats.map((f, fIdx) => {
                      const fPct = p.total > 0 ? ((f.count / p.total) * 100).toFixed(1) : '0.0';
                      return (
                        <motion.div
                          key={f.name}
                          className="flex items-center gap-3 pl-10 pr-3 py-1.5 hover:bg-slate-50/60 transition-colors rounded"
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.25,
                            delay: pIdx * 0.08 + fIdx * 0.04 + 0.1,
                            ease: [0.4, 0, 0.2, 1],
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-xs font-medium text-slate-600">{f.name}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs font-semibold text-slate-600 tabular-nums">
                                  {f.count}
                                </span>
                                <span className="text-[11px] text-slate-400 w-10 text-right tabular-nums">
                                  {fPct}%
                                </span>
                              </div>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${(f.count / p.total) * 100}%` }}
                                transition={{
                                  duration: 0.4,
                                  delay: pIdx * 0.08 + fIdx * 0.04 + 0.2,
                                  ease: [0.4, 0, 0.2, 1],
                                }}
                                style={{ backgroundColor: color, opacity: 0.6 }}
                              />
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <Treemap
                data={treemapData.children}
                dataKey="size"
                aspectRatio={4 / 3}
                stroke="#ffffff"
                animationDuration={600}
                content={({
                  x,
                  y,
                  width,
                  height,
                  name,
                  color,
                  size,
                  platform,
                }: {
                  x?: number;
                  y?: number;
                  width?: number;
                  height?: number;
                  name?: string;
                  color?: string;
                  size?: number;
                  platform?: string;
                }) => {
                  const w = width ?? 0;
                  const h = height ?? 0;
                  if (w < 2 || h < 2) return <g />;
                  const showLabel = w > 50 && h > 30;
                  const showCount = w > 50 && h > 45;
                  const platColor = color || getPlatformColor(platform || '');
                  return (
                    <g className="cursor-pointer">
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        rx={4}
                        fill={platColor}
                        opacity={0.75}
                        className="transition-opacity duration-200 hover:opacity-90"
                      />
                      {showLabel && (
                        <text
                          x={(x ?? 0) + w / 2}
                          y={(y ?? 0) + h / 2 - (showCount ? 6 : 0)}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="#ffffff"
                          fontSize={11}
                          fontWeight={500}
                        >
                          {name}
                        </text>
                      )}
                      {showCount && (
                        <text
                          x={(x ?? 0) + w / 2}
                          y={(y ?? 0) + h / 2 + 10}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="rgba(255,255,255,0.7)"
                          fontSize={10}
                        >
                          {size?.toLocaleString()}
                        </text>
                      )}
                    </g>
                  );
                }}
              >
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const entry = payload[0].payload as any;
                    const plat = entry.platform || entry.root?.name || '';
                    const formatTotal = platformFormatData.reduce((s, p) => s + p.total, 0);
                    const pct =
                      formatTotal > 0 ? ((entry.size / formatTotal) * 100).toFixed(1) : '0.0';
                    return (
                      <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600 min-w-[160px]">
                        <div className="flex items-center gap-2 font-semibold mb-2">
                          <PlatformIcon platform={plat} className="w-3.5 h-3.5" showLabel={false} />
                          <span>
                            {plat} — {entry.name}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <span className="text-slate-300">Campaigns:</span>
                          <span className="font-mono font-medium">
                            {entry.size?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <span className="text-slate-300">Share:</span>
                          <span className="font-mono font-medium">{pct}%</span>
                        </div>
                      </div>
                    );
                  }}
                />
              </Treemap>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
